import { useRef, type KeyboardEvent, type PointerEvent } from "react";
import { cn } from "@strivelabs/ui-core";
import { ChevronDown } from "lucide-react";
import { useComposer, useActiveMode, useModeFraction } from "./context";
import {
  COMPOSER_MODE_ACCENT,
  COMPOSER_MODE_GLOWS,
  COMPOSER_MODE_GRADIENT,
  sampleRamp,
} from "./tones";
import { Popover, Tooltip } from "@strivelabs/ui-core/v2";

/**
 * The Fast → Balanced → Deep Dive slider. Gradient fill tracks the active
 * mode; drag the track, click a stop, or use the arrow keys.
 */
export const ComposerModeSlider = () => {
  const { modes, mode, setMode, disabled } = useComposer();
  const trackRef = useRef<HTMLDivElement>(null);
  const count = modes.length;
  const activeIndex = Math.max(
    0,
    modes.findIndex((option) => option.id === mode),
  );
  const pct = count > 1 ? (activeIndex / (count - 1)) * 100 : 0;

  const setByClientX = (clientX: number) => {
    const el = trackRef.current;
    if (!el || count <= 1) return;
    const rect = el.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setMode(modes[Math.round(fraction * (count - 1))].id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (count <= 1) return;
    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      setMode(modes[Math.min(activeIndex + 1, count - 1)].id);
    } else if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      setMode(modes[Math.max(activeIndex - 1, 0)].id);
    } else if (event.key === "Home") {
      event.preventDefault();
      setMode(modes[0].id);
    } else if (event.key === "End") {
      event.preventDefault();
      setMode(modes[count - 1].id);
    }
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    trackRef.current?.setPointerCapture(event.pointerId);
    setByClientX(event.clientX);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.buttons !== 1 || disabled) return;
    setByClientX(event.clientX);
  };

  return (
    <div className="px-1 pt-1">
      <div
        ref={trackRef}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label="Response mode"
        aria-valuemin={0}
        aria-valuemax={Math.max(0, count - 1)}
        aria-valuenow={activeIndex}
        aria-valuetext={modes[activeIndex]?.label}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        className={cn(
          "relative mx-4 h-6 touch-none rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover",
          disabled ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing",
        )}
      >
        <div className="pointer-events-none absolute inset-x-0 top-2.5 h-1.5 rounded-full bg-muted" />
        <div
          className="pointer-events-none absolute inset-x-0 top-2.5 h-1.5 rounded-full transition-[clip-path] duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
          style={{
            background: COMPOSER_MODE_GRADIENT,
            clipPath: `inset(0 ${100 - pct}% 0 0 round 999px)`,
          }}
        />
        <div
          className="pointer-events-none absolute top-0.5 flex h-5 w-9 -translate-x-1/2 items-center justify-center gap-[3px] rounded-md border bg-background shadow-sm transition-[left] duration-[420ms] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
          style={{ left: `${pct}%` }}
        >
          <span
            className="h-2 w-0.5 rounded-sm"
            style={{ background: COMPOSER_MODE_ACCENT }}
          />
          <span
            className="h-2 w-0.5 rounded-sm"
            style={{ background: COMPOSER_MODE_ACCENT }}
          />
          <span
            className="h-2 w-0.5 rounded-sm"
            style={{ background: COMPOSER_MODE_ACCENT }}
          />
        </div>
      </div>
      <div className="mt-3 flex justify-between">
        {modes.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setMode(option.id)}
            className={cn(
              "rounded px-1 py-0.5 text-xs transition-colors",
              option.id === mode
                ? "font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export interface ComposerModeProps {
  className?: string;
}

/** Mode button showing the active label; opens the slider popover. */
export const ComposerMode = ({ className }: ComposerModeProps) => {
  const { modes, mode, openMenu, setOpenMenu, disabled } = useComposer();
  const active = useActiveMode();
  const fraction = useModeFraction();
  const stepColor = sampleRamp(COMPOSER_MODE_GLOWS, fraction);
  const activeIndex = Math.max(
    0,
    modes.findIndex((option) => option.id === mode),
  );
  const open = openMenu === "mode";

  return (
    <Tooltip label="Response mode" shortcut="⌘M" disabled={open}>
      <Popover
      open={open}
      onOpenChange={(next) => setOpenMenu(next ? "mode" : null)}
      aria-label="Response mode"
      contentClassName="w-[300px]"
      trigger={
        <button
          type="button"
          disabled={disabled}
          aria-label="Response mode"
          aria-haspopup="menu"
          aria-expanded={open}
          className={cn(
            "relative inline-flex h-9 items-center gap-2 rounded-lg bg-transparent px-2.5 text-xs font-medium text-foreground transition-[background-color,transform] before:absolute before:-inset-y-0.5 before:content-[''] hover:bg-accent active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50",
            open && "bg-accent",
            className,
          )}
        >
          <span className="flex items-end gap-[2px]" aria-hidden="true">
            {modes.map((option, index) => (
              <span
                key={option.id}
                className={cn(
                  "w-[3px] rounded-[1px] transition-colors duration-300",
                  index > activeIndex && "bg-border",
                )}
                style={{
                  height: `${5 + index * 3}px`,
                  background:
                    index <= activeIndex ? stepColor : undefined,
                }}
              />
            ))}
          </span>
          <span>{active?.label ?? "Mode"}</span>
          <ChevronDown
            className={cn(
              "size-3.5 text-muted-foreground transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>
      }
    >
      <div className="border-b border-border bg-muted/60 px-3 py-2.5">
        <div className="text-sm font-medium leading-tight text-foreground">
          Response mode
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          Choose how thorough the response is
        </div>
      </div>
      <div className="p-3">
        <ComposerModeSlider />
        {active?.description && (
          <p className="mt-3 px-1 text-xs leading-snug text-muted-foreground">
            {active.description}
          </p>
        )}
      </div>
      </Popover>
    </Tooltip>
  );
};
